import { hashKey, type QueryClient, type QueryKey } from '@tanstack/react-query';

/**
 * The writes to one cached list started and not yet settled, and the re-read
 * of the list that the last of them starts. Only the last write re-reads: a
 * refetch while another write is still out would put the server's old value
 * back on that write's optimistic row. The mutation cache cannot say "this is
 * the last write": a mutation leaves `pending` only after its awaited
 * callbacks, so two writes settling in the same tick each still see the other
 * and both skip the re-read. The count drops synchronously, so exactly one
 * write sees zero.
 */
interface WriteBatch {
  count: number;
  /**
   * Resolves once the list has been re-read after the last write. A write
   * that starts during that re-read opens a new batch and the wait passes on
   * to it: a row write cancels the re-read, so the old rows stay until the new
   * batch re-reads.
   */
  reread: Promise<void>;
  resolveReread: (invalidation: Promise<void>) => void;
}

const batches = new WeakMap<QueryClient, Map<string, WriteBatch>>();

function batchesOf(queryClient: QueryClient): Map<string, WriteBatch> {
  let byList = batches.get(queryClient);
  if (!byList) {
    byList = new Map();
    batches.set(queryClient, byList);
  }
  return byList;
}

function joinBatch(queryClient: QueryClient, listKey: QueryKey) {
  const byList = batchesOf(queryClient);
  const hash = hashKey(listKey);
  let batch = byList.get(hash);
  if (!batch) {
    let resolveReread!: WriteBatch['resolveReread'];
    const reread = new Promise<void>((resolve) => {
      resolveReread = resolve;
    });
    batch = { count: 0, reread, resolveReread };
    byList.set(hash, batch);
  }
  batch.count += 1;
}

/**
 * Leaves the batch; the last write to leave starts the re-read, failed or
 * not, since a write that settled earlier left its re-read to it.
 */
function leaveBatch(queryClient: QueryClient, listKey: QueryKey) {
  const byList = batchesOf(queryClient);
  const hash = hashKey(listKey);
  const batch = byList.get(hash)!;
  batch.count -= 1;
  const last = batch.count === 0;
  if (last) {
    byList.delete(hash);
    batch.resolveReread(
      queryClient.invalidateQueries({ queryKey: listKey }).then(() => byList.get(hash)?.reread)
    );
  }
  return { last, reread: batch.reread };
}

/** The mutation context of one write to a cached list. */
export interface ListWrite {
  listKey: QueryKey;
}

interface AppRow {
  appId: string;
}

/** The mutation context of one optimistic row write. */
export interface RowWrite<TRow extends AppRow> extends ListWrite {
  appId: string;
  /** The patched fields as they were before the write. */
  previous?: Partial<TRow>;
}

export function patchRow<TRow extends AppRow>(
  queryClient: QueryClient,
  { listKey, appId }: Pick<RowWrite<TRow>, 'listKey' | 'appId'>,
  patch: Partial<TRow>
) {
  queryClient.setQueryData<TRow[]>(listKey, (rows) =>
    rows?.map((row) => (row.appId === appId ? { ...row, ...patch } : row))
  );
}

/**
 * Writes `patch` onto the one cached row before the server confirms it. Call
 * it from `onMutate` and return the result as the mutation context. Only the
 * patched fields are snapshotted: a sibling row's write may be pending in the
 * same list, and restoring a whole-list snapshot would take it back too.
 */
export async function beginRowWrite<TRow extends AppRow>(
  queryClient: QueryClient,
  target: Omit<RowWrite<TRow>, 'previous'>,
  patch: Partial<TRow>
): Promise<RowWrite<TRow>> {
  joinBatch(queryClient, target.listKey);
  await queryClient.cancelQueries({ queryKey: target.listKey });
  const row = queryClient
    .getQueryData<TRow[]>(target.listKey)
    ?.find((candidate) => candidate.appId === target.appId);
  let previous: Partial<TRow> | undefined;
  if (row) {
    previous = {};
    for (const field of Object.keys(patch) as (keyof TRow)[]) previous[field] = row[field];
  }
  patchRow(queryClient, target, patch);
  return { ...target, previous };
}

/**
 * Settles the write: a failed one puts back the fields it patched. Returned
 * from `onSuccess`, the promise keeps the last write pending until the
 * server's rows have replaced the optimistic ones; an earlier write settles at
 * once, and the last write's re-read brings its row too.
 */
export function endRowWrite<TRow extends AppRow>(
  queryClient: QueryClient,
  write: RowWrite<TRow> | undefined,
  error: unknown
): Promise<void> | undefined {
  if (!write) return undefined;
  if (error && write.previous) patchRow(queryClient, write, write.previous);
  const { last, reread } = leaveBatch(queryClient, write.listKey);
  return last ? reread : undefined;
}

/**
 * Counts a write that patches nothing into the list's batch, so it re-reads
 * the list only as the last write and an optimistic row write settling
 * meanwhile leaves its re-read to it.
 */
export function beginListWrite(queryClient: QueryClient, listKey: QueryKey): ListWrite {
  joinBatch(queryClient, listKey);
  return { listKey };
}

/**
 * Settles a write started with `beginListWrite`. Returned from `onSuccess`,
 * the promise keeps this write pending until the list has been re-read after
 * the batch's last write, even when a later write starts that re-read.
 */
export function endListWrite(
  queryClient: QueryClient,
  write: ListWrite | undefined
): Promise<void> | undefined {
  if (!write) return undefined;
  return leaveBatch(queryClient, write.listKey).reread;
}
